import type {
  CreateInvoiceLineItemInputType,
  CurrencyBalance,
  Product,
  User,
  UserBalances,
} from 'payments-api/model';
import type { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import {
  currencyCodes,
  currencyPrecision as currencyPrecisionMap,
} from '@fragment/types/currencyCodes';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Icon } from '@fragment/ui/src/components/Icon';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';

import { getRoleColor, ITEM_COLOR_CLASS } from './constants';
import { CreateProductModal } from './CreateProductModal';
import { CreateUserModal } from './CreateUserModal';
import { FormatBillingAmount } from './formatBillingAmount';
import { getUsersForProductByItemType } from './invoiceItemUtils';

const CREATE_PRODUCT_SENTINEL = '__create_product__';
const CREATE_USER_SENTINEL = '__create_user__';
/** Suffix to strip from line item descriptions when displaying on invoice detail. */
const USERS_SHARE_PLACEHOLDER_SUFFIX = ' - Users share (placeholder)';

const formatItemDescription = (description: string | undefined): string => {
  const s = description?.trim() ?? '';
  const suffixLen = USERS_SHARE_PLACEHOLDER_SUFFIX.length;
  if (
    s.length >= suffixLen &&
    s.toLowerCase().endsWith(USERS_SHARE_PLACEHOLDER_SUFFIX.toLowerCase())
  ) {
    return s.slice(0, -suffixLen).trim();
  }
  return s;
};

export type ItemTableItem = {
  id: string;
  paid?: number;

  /** The type of the line item */
  type: CreateInvoiceLineItemInputType;
  /** ID of the product/catalog item */
  product_id: string;
  /** Amount in smallest currency unit (e.g., cents) */
  amount: string;
  currencyCode: CurrencyCode;
  /** Description of the line item */
  description: string;
  user_id: string;
};

const ItemRow = ({
  item,
  index,
  isLast,
  onRemove,
  onEdit,
}: {
  item: ItemTableItem;
  index: number;
  isLast: boolean;
  onRemove?: () => void;
  onEdit?: () => void;
}) => {
  const amountNum = Number(item.amount);
  const precision =
    currencyPrecisionMap[item.currencyCode as CurrencyCode] || 2;
  return (
    <div
      className={clsx(
        'flex flex-row w-full items-stretch border-main-200',
        !isLast && 'border-b-[0.5px]'
      )}
      data-testid={`item-row-${index}`}
    >
      <div className="flex items-center justify-center w-[2lh] min-w-[2lh] shrink-0 text-main-500 font-FragmentMono text-xmd border-r-[0.5px] border-main-200 aspect-square">
        {item.type === 'payin' ? '↗' : '↙'}
      </div>
      <div className="flex items-center gap-x-f1 p-f1 min-w-0 overflow-hidden shrink-0">
        <div
          className={clsx(
            'w-[14px] h-[14px] shrink-0 rounded-[2px]',
            ITEM_COLOR_CLASS
          )}
          aria-hidden
        />
        <span className="text-main truncate text-left">
          {item.product_id || '—'}
        </span>
      </div>
      <div className="flex items-center p-f1 min-w-0 flex-1 overflow-hidden">
        <span className="text-main-500 text-xmd truncate text-left block">
          {formatItemDescription(item.description)}
        </span>
      </div>
      <div className="flex flex-row justify-end items-center gap-x-f1 p-f1 shrink-0 overflow-hidden">
        <FormatBillingAmount
          amount={amountNum}
          currencyCode={item.currencyCode as CurrencyCode}
          currencyPrecision={precision}
          secondary
        />
        {onEdit && (
          <TextButton onClick={onEdit} data-testid={`item-row-${index}-edit`}>
            Edit
          </TextButton>
        )}
        {onRemove && (
          <TextButton
            onClick={onRemove}
            data-testid={`item-row-${index}-remove`}
            className="text-main-500 hover:text-main text-xmd w-fit text-left"
            icon={<Icon type="trash" size="md" primaryColor="gray" />}
            hoverIcon={<Icon type="trash" size="md" primaryColor="black" />}
          />
        )}
      </div>
    </div>
  );
};

const UserBalanceBreakdown = ({
  balances,
}: {
  balances: CurrencyBalance[];
}) => (
  <div className="flex flex-col gap-y-f1 mt-f1">
    {balances.map((balance) => {
      const precision =
        currencyPrecisionMap[balance.currency as CurrencyCode] || 2;
      const code = balance.currency as CurrencyCode;
      const expected = Number(balance.net.expected);
      const actual = Number(balance.net.actual);
      const remaining = Number(balance.net.remaining);
      return (
        <div key={balance.currency} className="flex flex-col gap-y-f1">
          <span className="flex items-baseline gap-x-f1 justify-end">
            <span className="text-main-500 text-xmd">Expected</span>
            <FormatBillingAmount
              amount={expected}
              currencyPrecision={precision}
              currencyCode={code}
              secondary={expected === 0}
            />
          </span>
          <span className="flex items-baseline gap-x-f1 justify-end">
            <span className="text-main-500 text-xmd">Actual</span>
            <FormatBillingAmount
              amount={actual}
              currencyPrecision={precision}
              currencyCode={code}
              secondary={actual === 0}
            />
          </span>
          <span className="flex items-baseline gap-x-f1 justify-end">
            <span className="text-main-500 text-xmd">Remaining</span>
            <FormatBillingAmount
              amount={remaining}
              currencyPrecision={precision}
              currencyCode={code}
              secondary={remaining === 0}
            />
          </span>
        </div>
      );
    })}
  </div>
);

const UserGroup = ({
  userId,
  items,
  startIndex,
  balances,
  users,
  onRemove,
  onEdit,
}: {
  userId: string;
  items: ItemTableItem[];
  startIndex: number;
  balances?: CurrencyBalance[];
  users?: User[];
  onRemove?: (id: string) => void;
  onEdit?: (id: string) => void;
}) => {
  const user = users?.find((u) => u.externalId === userId);
  const roleColor = user ? getRoleColor(user.role) : 'bg-main-200';
  return (
    <div className="flex flex-row w-full border-b-[0.5px] border-main-200">
      <div
        className={clsx(
          kLabelColumnWidth,
          'shrink-0 p-f1 border-r-[0.5px] border-main-200 flex items-start min-w-0'
        )}
      >
        <div className="flex items-center gap-x-f1">
          <div
            className={clsx(
              'w-[14px] h-[14px] rounded-full shrink-0',
              roleColor
            )}
          />
          <span className="text-main text-xmd truncate">{userId || '—'}</span>
        </div>
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        {balances && balances.length > 0 && (
          <div className="flex flex-row justify-end gap-x-f1 p-f1 border-b-[0.5px] border-main-200">
            {balances.map((balance) => {
              const amt = Number(balance.net.expected);
              return (
                <FormatBillingAmount
                  key={balance.currency}
                  amount={amt}
                  currencyPrecision={
                    currencyPrecisionMap[balance.currency as CurrencyCode] || 2
                  }
                  currencyCode={balance.currency as CurrencyCode}
                  secondary={amt === 0}
                />
              );
            })}
          </div>
        )}
        {items.map((item, i) => (
          <ItemRow
            key={item.id}
            item={item}
            index={startIndex + i}
            isLast={i === items.length - 1}
            onRemove={onRemove ? () => onRemove(item.id) : undefined}
            onEdit={onEdit ? () => onEdit(item.id) : undefined}
          />
        ))}
      </div>
    </div>
  );
};

const DraftItemRow = ({
  products,
  users,
  initialValues,
  onSubmit,
  onUpdate,
  onCancel,
}: {
  products: Product[];
  users: User[];
  initialValues: ItemTableItem | null;
  onSubmit: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdate: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onCancel: () => void;
}) => {
  const [itemType, setItemType] = useState<InvoiceLineItemType>(
    initialValues?.type || 'payin'
  );
  const isEdit = initialValues !== null;
  const [productCode, setProductCode] = useState(
    isEdit ? initialValues.product_id : ''
  );
  const [userId, setUserId] = useState(isEdit ? initialValues.user_id : '');
  const [description, setDescription] = useState(
    isEdit ? initialValues.description : ''
  );
  const [amount, setAmount] = useState(isEdit ? initialValues.amount : '');
  const [currencyCode, setCurrencyCode] = useState<string>(
    isEdit ? initialValues.currencyCode : 'USD'
  );
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  const handleProductChange = useCallback((code: string) => {
    setProductCode(code);
    setUserId('');
  }, []);

  const handleProductDropdownChange = useCallback(
    (value: string) => {
      if (value === CREATE_PRODUCT_SENTINEL) {
        setShowCreateProductModal(true);
      } else {
        handleProductChange(value);
      }
    },
    [handleProductChange]
  );

  const handleProductCreated = useCallback(
    (code: string) => {
      handleProductChange(code);
      setShowCreateProductModal(false);
    },
    [handleProductChange]
  );

  const handleUserDropdownChange = useCallback((value: string) => {
    if (value === CREATE_USER_SENTINEL) {
      setShowCreateUserModal(true);
    } else {
      setUserId(value);
    }
  }, []);

  const handleUserCreated = useCallback((externalId: string) => {
    setUserId(externalId);
    setShowCreateUserModal(false);
  }, []);

  const productOptions = [
    ...products.map((p) => ({
      value: p.code,
      label: `${p.code} - ${p.description}`,
    })),
    { value: CREATE_PRODUCT_SENTINEL, label: 'New+' },
  ];

  const userOptions = [
    ...getUsersForProductByItemType(productCode, itemType, products, users),
    { value: CREATE_USER_SENTINEL, label: 'New+' },
  ];

  const handleSubmit = useCallback(() => {
    const item: Omit<ItemTableItem, 'id'> = {
      type: itemType,
      product_id: productCode,
      user_id: userId,
      amount,
      currencyCode: currencyCode as CurrencyCode,
      description,
    };
    if (isEdit) {
      onUpdate(initialValues.id, item);
    } else {
      onSubmit(item);
    }
    onCancel();
  }, [
    itemType,
    productCode,
    userId,
    currencyCode,
    description,
    amount,
    isEdit,
    initialValues,
    onSubmit,
    onUpdate,
    onCancel,
  ]);

  const canSubmit =
    productCode.trim() !== '' &&
    userId.trim() !== '' &&
    amount.trim() !== '' &&
    !Number.isNaN(Number(amount));

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="flex flex-col gap-y-f1 w-full border-main-200 border-[1px] border-t-0 p-f1 min-h-f12"
      >
        <div className="flex flex-row gap-x-f1 w-full">
          <span className="text-main text-xmd min-w-f6">Type</span>
          <Dropdown
            options={[
              { value: 'payin', label: 'Payin' },
              { value: 'payout', label: 'Payout' },
            ]}
            onChange={(value) => {
              setItemType(value as InvoiceLineItemType);
              setProductCode('');
              setUserId('');
            }}
            displayValue={itemType}
            size="md"
            width="w-full"
            height="h-fit"
            align="left"
            data-testid="draft-item-type-dropdown"
          />
        </div>
        <div className="flex flex-row gap-x-f1 w-full">
          <span className="text-main text-xmd min-w-f6">Product</span>
          <Dropdown
            options={productOptions}
            onChange={handleProductDropdownChange}
            displayValue={productCode || 'Select a product'}
            size="md"
            width="w-full"
            height="h-fit"
            align="left"
            data-testid="draft-item-product-dropdown"
          />
        </div>
        <div className="flex flex-row gap-x-f1 w-full">
          <span className="text-main text-xmd min-w-f6">User</span>
          <Dropdown
            options={userOptions}
            onChange={handleUserDropdownChange}
            displayValue={userId || 'Select a user'}
            size="md"
            width="w-full"
            height="h-fit"
            align="left"
            data-testid="draft-item-user-dropdown"
          />
        </div>
        <TextInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
          size="md"
          width="w-full"
          height="h-fit"
          padding="p-0"
          align="left"
          data-testid="draft-item-description-input"
        />
        <div className="flex flex-row gap-x-f1 w-full">
          <span className="text-main text-xmd min-w-f6">Amount</span>
          <TextInput
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            size="md"
            width="w-full"
            height="h-fit"
            padding="p-0"
            align="left"
            data-testid="draft-item-amount-input"
          />
        </div>
        <div className="flex flex-row gap-x-f1 w-full">
          <span className="text-main text-xmd min-w-f6">Currency</span>
          <Dropdown
            options={currencyCodes
              .filter((code) => code !== 'LOGICAL' && code !== 'CUSTOM')
              .map((code) => ({
                value: code,
                label: code,
              }))}
            onChange={setCurrencyCode}
            displayValue={currencyCode}
            size="md"
            width="w-full"
            height="h-fit"
            align="left"
            data-testid="draft-item-currency-dropdown"
          />
        </div>
        <FormButtons
          type="secondary"
          cancelButtonLabel="Cancel"
          onCancel={onCancel}
          submitButtonLabel={isEdit ? 'Save' : 'Add+'}
          disabled={!canSubmit}
          submitButtonTestId="draft-item-submit-button"
          cancelButtonTestId="draft-item-cancel-button"
        />
      </form>
      {showCreateProductModal && (
        <CreateProductModal
          onClose={() => setShowCreateProductModal(false)}
          onCreated={handleProductCreated}
        />
      )}
      {showCreateUserModal && (
        <CreateUserModal
          onClose={() => setShowCreateUserModal(false)}
          onCreated={handleUserCreated}
        />
      )}
    </>
  );
};

const NewItemRow = ({ onAdd }: { onAdd: () => void }) => (
  <button
    type="button"
    className="flex w-full h-full p-f1 bg-main-200 border-main-200 border-[1px] border-t-0  hover:text-main hover:bg-negative text-center items-center justify-center"
    onClick={onAdd}
    aria-label="Add new item"
    data-testid="add-new-item-button"
  >
    Add New Item+
  </button>
);

type ItemTableProps = {
  items: ItemTableItem[];
  products: Product[];
  users: User[];
  onAddItem?: (item: Omit<ItemTableItem, 'id'>) => void;
  onUpdateItem?: (id: string, item: Omit<ItemTableItem, 'id'>) => void;
  onRemoveItem?: (id: string) => void;
  userBalances?: UserBalances[];
  'data-testid'?: string;
};

export const ItemTable = ({
  items,
  products,
  users,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  userBalances,
  'data-testid': dataTestId,
}: ItemTableProps): JSX.Element => {
  const [showDraftItem, setShowDraftItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const editingItem =
    editingItemId !== null
      ? items.find((i) => i.id === editingItemId) ?? null
      : null;

  const handleAddItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      onAddItem?.(item);
      setShowDraftItem(false);
      setEditingItemId(null);
    },
    [onAddItem]
  );

  const handleUpdateItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      onUpdateItem?.(id, item);
      setShowDraftItem(false);
      setEditingItemId(null);
    },
    [onUpdateItem]
  );

  const handleCancelDraft = useCallback(() => {
    setShowDraftItem(false);
    setEditingItemId(null);
  }, []);

  const handleStartEdit = useCallback((id: string) => {
    setEditingItemId(id);
    setShowDraftItem(true);
  }, []);

  const handleStartAdd = useCallback(() => {
    setEditingItemId(null);
    setShowDraftItem(true);
  }, []);

  const sortedItems = items
    .filter((item) => item.id !== editingItemId)
    .sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'payin' ? -1 : 1;
    });

  // Group items by user_id, preserving order of first appearance
  const userGroups: { userId: string; items: ItemTableItem[] }[] = [];
  const seen = new Map<string, ItemTableItem[]>();
  sortedItems.forEach((item) => {
    const uid = item.user_id || '';
    if (!seen.has(uid)) {
      const group: ItemTableItem[] = [];
      seen.set(uid, group);
      userGroups.push({ userId: uid, items: group });
    }
    seen.get(uid)!.push(item);
  });

  // Compute start indices for data-testid continuity
  const groupStartIndices = userGroups.reduce<number[]>((acc, _, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + userGroups[i - 1].items.length);
    return acc;
  }, []);

  return (
    <div data-testid={dataTestId} className="flex flex-col h-fit w-full">
      {!showDraftItem && onAddItem && <NewItemRow onAdd={handleStartAdd} />}
      {showDraftItem && (onAddItem || onUpdateItem) && (
        <DraftItemRow
          key={editingItemId ?? 'add'}
          products={products}
          users={users}
          initialValues={editingItem}
          onSubmit={handleAddItem}
          onUpdate={handleUpdateItem}
          onCancel={handleCancelDraft}
        />
      )}
      {userGroups.length > 0 && (
        <div className="flex flex-row w-full p-f1 border-t-[0.5px] border-b-[0.5px] border-main-200 min-h-[1lh]">
          <span
            className={clsx(
              kLabelColumnWidth,
              'shrink-0 text-main-500 text-xmd'
            )}
          >
            Users
          </span>
          <div className="flex flex-row flex-1 justify-between min-w-0">
            <span className="text-main-500 text-xmd">Items</span>
            <span className="text-main-500 text-xmd">Due</span>
          </div>
        </div>
      )}
      {userGroups.map(({ userId, items: groupItems }, groupIndex) => (
        <UserGroup
          key={userId}
          userId={userId}
          items={groupItems}
          startIndex={groupStartIndices[groupIndex]}
          balances={userBalances?.find((u) => u.id === userId)?.balances}
          users={users}
          onRemove={onRemoveItem}
          onEdit={onUpdateItem ? handleStartEdit : undefined}
        />
      ))}
    </div>
  );
};
